import type { CompleteStoreFragment } from '@drycut/graphql';

import { useQuery } from '@apollo/client/react';
import { StoreWaitingTime } from '@drycut/controlled-components';
import { GetStoreWaitingTimeDocument } from '@drycut/graphql';
import { Para, Stack } from '@drycut/ui-components';
import { MAX_MINUTES_TO_DISPLAY } from '@drycut/ui-format-utils';
import { twJoin } from 'tailwind-merge';

import { useCheckoutStore } from '../../checkoutStore';
import { StylistList } from './StylistList';

type Props = {
  manual?: boolean;
  store: CompleteStoreFragment;
};

export default function WaitingTimeDetails({ store, manual = false }: Props): React.ReactElement {
  const selectedServices = useCheckoutStore((state) => state.selectedServices);
  const serviceIds = selectedServices.map((service) => service.id);

  // Once the customer has (at least a default) service selected, ask the server for the waiting
  // time for that specific combination instead of the store's generic estimate. This keeps the
  // displayed estimate accurate right up to booking, instead of it looking fine here and then
  // jumping once the booking is actually created (which happens if the store is about to close and
  // the selected services wouldn't fit in the remaining sliver of time).
  //
  // `waitingTime` is relative to the moment the server computed it, and StoreWaitingTime turns it
  // into a clock time using the current time. So it must always be fresh (network-only, even
  // without services selected): a cached or stale value on a long-open page (e.g. the manual
  // kiosk) would make the displayed time drift later on every mount.
  const { data } = useQuery(GetStoreWaitingTimeDocument, {
    variables: { slug: store.slug, serviceIds: serviceIds.length > 0 ? serviceIds : undefined },
    fetchPolicy: 'network-only',
  });

  const waitingTime = data?.store?.waitingTime ?? store.waitingTime;
  const longerWait = waitingTime > MAX_MINUTES_TO_DISPLAY * 60;

  const waitingTimeClassName = twJoin('font-aribaubold text-primary-700', manual && 'text-3xl');

  return (
    <Stack gap={2}>
      {!longerWait && (
        <StylistList stylists={store.checkedInStylists} size={manual ? 'base' : 'sm'} />
      )}

      <Stack gap={1}>
        {!longerWait ? (
          <Para size={manual ? 'base' : 'sm'} pretty>
            Der nächste verfügbare Cut in
          </Para>
        ) : (
          <Para size={manual ? 'base' : 'sm'}>Nächster freier Termin in</Para>
        )}
        <Para as="span" className={waitingTimeClassName}>
          <StoreWaitingTime store={{ ...store, waitingTime }} showCa showZero steps />
        </Para>
      </Stack>
    </Stack>
  );
}
